/** Normalise SA-style registrations for lookup (uppercase, strip spaces/punctuation). */
export function normalizeLicensePlate(raw) {
  if (raw == null || raw === '') return ''
  return String(raw)
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
}

const SA_PROVINCE_SUFFIXES = ['GP', 'WC', 'EC', 'KZN', 'FS', 'LP', 'MP', 'NW', 'NC']

/** Common OCR misreads on plates — apply before normalising. */
function fixOcrPlateText(text) {
  return String(text || '')
    .toUpperCase()
    .replace(/[|\\]/g, 'I')
    .replace(/[`'"]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

function scorePlateCandidate(norm) {
  if (!norm || norm.length < 4 || norm.length > 12) return 0
  let score = norm.length
  const hasLetter = /[A-Z]/.test(norm)
  const hasDigit = /[0-9]/.test(norm)
  if (hasLetter && hasDigit) score += 14
  else if (hasLetter || hasDigit) score += 4
  if (norm.length >= 6 && norm.length <= 9) score += 4
  for (const suffix of SA_PROVINCE_SUFFIXES) {
    if (norm.endsWith(suffix)) {
      score += 8
      break
    }
  }
  if (/^([A-Z]{2,3})(\d{2,6})([A-Z]{0,3})?$/.test(norm)) score += 6
  if (/^\d+[A-Z]+\d*$/.test(norm) || /^[A-Z]+\d+[A-Z]*$/.test(norm)) score += 3
  return score
}

function addCandidate(set, raw) {
  const norm = normalizeLicensePlate(fixOcrPlateText(raw))
  if (norm.length >= 4 && norm.length <= 12) set.add(norm)
}

/** Pick the most likely plate string from raw OCR output. */
export function extractPlateCandidates(ocrText) {
  const text = fixOcrPlateText(ocrText)
  const candidates = new Set()

  if (!text) return []

  // Continuous alphanumeric runs (most reliable for OCR output)
  const runs = text.match(/[A-Z0-9]{4,12}/g) || []
  runs.forEach((run) => addCandidate(candidates, run))

  const spacedRuns = text.match(/[A-Z0-9][A-Z0-9\s\-]{2,14}[A-Z0-9]/g) || []
  spacedRuns.forEach((chunk) => addCandidate(candidates, chunk))

  const tokens = text.split(/[^A-Z0-9]+/).filter(Boolean)
  tokens.forEach((token) => addCandidate(candidates, token))

  for (let i = 0; i < tokens.length; i += 1) {
    for (let len = 2; len <= 4 && i + len <= tokens.length; len += 1) {
      addCandidate(candidates, tokens.slice(i, i + len).join(''))
    }
  }

  addCandidate(candidates, text)

  // OCR often splits digits/letters — merge adjacent tokens
  for (let i = 0; i < tokens.length - 1; i += 1) {
    addCandidate(candidates, tokens[i] + tokens[i + 1])
    if (i + 2 < tokens.length) addCandidate(candidates, tokens[i] + tokens[i + 1] + tokens[i + 2])
  }

  return Array.from(candidates)
    .map((norm) => ({ norm, score: scorePlateCandidate(norm) }))
    .filter((x) => x.score > 0 && /[0-9]/.test(x.norm))
    .sort((a, b) => b.score - a.score)
    .map((x) => x.norm)
}

export function platesEquivalent(a, b) {
  const na = normalizeLicensePlate(a)
  const nb = normalizeLicensePlate(b)
  if (!na || !nb) return false
  return na === nb
}
